import api from '@/api'
import Vue from 'vue'
import i18n from '@/plugins/i18n.plugin'
import { defineSyncSchema, schemaKinds, createSyncState, createSyncRequest, mergeSyncResponse, recordWrite, syncMutations } from '@/helpers/storeSync'

const ROL_SYNC_SCHEMA = defineSyncSchema({ key: 'ID_ROL', kind: 'rol', insertAt: 'end', overlays: [{ kind: 'rolPermisos', fields: ['PERMISOS'] }] })

const state = {
    roles: [],
    sync: createSyncState(schemaKinds(ROL_SYNC_SCHEMA)),
}

const getters = {
    getRoles: (state) => state.roles,
    getRolById: (state) => (id) => state.roles.find(x => x.ID_ROL === id),
    getRolPredeterminado: (state) => () => state.roles.find(x => x.ES_PREDETERMINADO)
}

const mutations = {
    ...syncMutations,

    MERGE_ROLES(state, { rows, request }) {
        state.roles = mergeSyncResponse(state.sync, state.roles, rows, ROL_SYNC_SCHEMA, request)
    },

    SOCKET_addRol(state, data) {
        state.roles.push(data)
        recordWrite(state.sync, 'rol', data.ID_ROL)
        this.notify(i18n.t('alert.roleAdded'), i18n.t('alert.roleAdd', { rol: data.ROL }), 'success')
    },

    SOCKET_addRolPermiso(state, data) {
        if (data.ID_ROL) {
            const rolIndex = state.roles.findIndex(x => x.ID_ROL === data.ID_ROL)
            if (rolIndex !== -1) {
                const rol = state.roles[rolIndex]
                rol.PERMISOS = []
                Vue.set(state.roles, rol.ID_ROL, rol)
                recordWrite(state.sync, 'rolPermisos', rol.ID_ROL)
                this.notify(i18n.t('alert.permissionAdded'), i18n.t('alert.permissionRoleDeleted', { rol: rol.ROL }), 'success')
            }
            return
        }
        const rolIndex = state.roles.findIndex(x => x.ID_ROL === data[0].ID_ROL)
        if (rolIndex !== -1) {
            const rol = state.roles[rolIndex]

            rol.PERMISOS = data
            Vue.set(state.roles, rol.ID_ROL, rol)
            recordWrite(state.sync, 'rolPermisos', rol.ID_ROL)
            this.notify(i18n.t('alert.permissionAdded'), i18n.t('alert.permissionRoleAdded', { rol: rol.ROL }), 'success')
        }
    },

    SOCKET_updateRol(state, data) {
        const rolIndex = state.roles.findIndex(x => x.ID_ROL === data.ID_ROL)
        if (rolIndex !== -1) {
            const rol = state.roles[rolIndex]
            Vue.set(state.roles, rolIndex, data)
            recordWrite(state.sync, 'rol', data.ID_ROL)
            this.notify(i18n.t('alert.roleUpdated'), i18n.t('alert.roleUpdate', { rol: rol.ROL }), 'success')
        }
    },

    SOCKET_updateEstadoRol(state, data) {
        const rolIndex = state.roles.findIndex(x => x.ID_ROL === data.ID_ROL)
        if (rolIndex !== -1) {
            const rol = state.roles[rolIndex]
            rol.BORRADO = data.BORRADO
            Vue.set(state.roles, rolIndex, rol)
            recordWrite(state.sync, 'rol', data.ID_ROL)
            this.notify(i18n.t('alert.roleUpdated'), i18n.t('alert.roleStateUpdate', { rol: rol.ROL }), 'success')
        }
    },

    SOCKET_deleteRolPermiso(state, idRolPermiso) {
        for (const rol of state.roles) {
            const rolIndex = rol.PERMISOS.findIndex(
                x => x.ID_ROL_PERMISO === idRolPermiso
            );
            if (rolIndex !== -1) {
                rol.PERMISOS.splice(rolIndex, 1);
                recordWrite(state.sync, 'rolPermisos', rol.ID_ROL)
                this.notify(
                    i18n.t('alert.permissionDeleted'),
                    i18n.t('alert.permissionDelete', { rol: rol.ROL }),
                    'success'
                );
                break;
            }
        }
    }

}

const actions = {
    async fetchRoles({ commit, state }) {
        try {
            commit('BEGIN_SYNC_REQUEST')
            const request = createSyncRequest(state.sync.seq)
            const response = await api.rol.getRoles()
            commit('MERGE_ROLES', { rows: response.data, request })
        } catch (err) {
            console.error(i18n.t('alert.error.roleRetrieval'), err)
            throw err
        }
    },

    async addRol({ commit }, rol) {
        try {
            await api.rol.addRol(rol)
        } catch (err) {
            console.error(i18n.t('alert.error.roleAddition'), err)
            throw err
        }
    },

    async updateRol({ commit }, rol) {
        try {
            await api.rol.updateRol(rol)
        } catch (err) {
            console.error(i18n.t('alert.error.roleUpdate'), err)
            throw err
        }
    },

    async addRolPermiso({ commit }, rolPermiso) {
        try {
            await api.rol.addRolPermiso([rolPermiso])
        } catch (err) {
            console.error(i18n.t('alert.error.roleAddition'), err)
            throw err
        }
    },

    async updateEstadoRol({ commit }, rolData) {
        try {
            await api.rol.updateEstadoRol(rolData)
        } catch (err) {
            console.error(i18n.t('alert.error.roleStateUpdate'), err)
            throw err
        }
    },

    async duplicarRol({ commit }, rolId) {
        try {
            await api.rol.duplicateRol(rolId)
        } catch (err) {
            console.error(i18n.t('alert.error.duplicateRole'), err)
            throw err
        }
    },

    async deleteRolPermiso({ commit }, rolPermisoId) {
        try {
            await api.rol.deleteRolPermiso(rolPermisoId)
        } catch (err) {
            console.error(i18n.t('alert.error.rolePermissionDelete'), err)
            throw err
        }
    }
}

const module = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
}

export default module
